import { useCallback, useEffect, useRef, useState } from 'react';
import { MdPlayArrow, MdPause } from 'react-icons/md';
import { useIdleTimer } from 'react-idle-timer';
import { Tooltip } from '@components/Tooltip';
import { useEvent } from '@hooks/useEvent';
import {
  createTimeTracking as createTimeTrackingService,
  updateTimeTracking as updateTimeTrackingService,
} from './services';
import { TimeTrackerActionButton, TimeTrackerContainer, TimeTrackerValue } from './styles';
import {
  ActivityType,
  CreateTimeTrackingRecord,
  EndReason,
  StartReason,
  TrackingType,
  UpdateTimeTrackingRecord,
} from './types';
import { formatSeconds } from './helpers';
import { RootStateOrAny, useSelector } from 'react-redux';
import { mentorRoles } from '@shared/constants/roles';
import { subscribeToTimeTrackerLogout } from './events';

const KEEP_ALIVE_FREQUENCY_MS = Number(process.env.TIME_TRACK_KEEP_ALIVE_FREQUENCY_MS || 60000);

export type TimeTrackerProps = {
  userId: string;
  activityId: string | null;
  activityType: ActivityType;
  hidden?: boolean;
};

const TimeTracker = ({ activityType, activityId, userId, hidden = false }: TimeTrackerProps) => {
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const displayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isStartingRef = useRef(false);
  const isTrackingActiveRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const savedTrackingIdRef = useRef<string | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [watchPaused, setWatchPaused] = useState(false);

  const {
    roles = [],
    id: mentorId,
    enableTimeTracking,
  } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS());

  const isClinicalMentor = roles.some((roleId: string) => mentorRoles.includes(roleId));
  const isTimeTrackingEnabled = mentorId && isClinicalMentor && enableTimeTracking;

  const idleTimer = useIdleTimer({});

  const getActiveSeconds = useEvent(() => Math.ceil(idleTimer.getElapsedTime() / 1000));

  const startDisplayInterval = useCallback(() => {
    displayIntervalRef.current = setInterval(() => {
      setDisplaySeconds(getActiveSeconds());
    }, 500);
  }, []);

  const stopDisplayInterval = useCallback(() => {
    if (displayIntervalRef.current) {
      clearInterval(displayIntervalRef.current);
      displayIntervalRef.current = null;
    }
  }, []);

  const startKeepAliveInterval = useCallback(() => {
    keepAliveIntervalRef.current = setInterval(() => {
      updateRecord(EndReason.KEEPALIVE);
    }, KEEP_ALIVE_FREQUENCY_MS);
  }, []);

  const stopKeepAliveInterval = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const createTimeTracking = useCallback(async (record: CreateTimeTrackingRecord) => {
    try {
      const createdRecord = await createTimeTrackingService(record);
      const createdId = createdRecord?._id || null;
      if (createdId) {
        savedTrackingIdRef.current = createdId;
      }
      return createdRecord;
    } catch (error) {}
  }, []);

  const updateTimeTracking = useCallback(async (record: UpdateTimeTrackingRecord) => {
    try {
      const updatedRecord = await updateTimeTrackingService(record);
      return updatedRecord;
    } catch (error) {}
  }, []);

  const updateRecord = useEvent(async (endReason: EndReason) => {
    if (isLoggingOutRef.current || !savedTrackingIdRef.current) {
      return null;
    }

    const nowIso = new Date().toISOString();
    const totalSeconds = getActiveSeconds();
    const record = {
      _id: savedTrackingIdRef.current,
      endTime: nowIso,
      endReason,
      duration: totalSeconds,
    };
    const updatedRecord = await updateTimeTracking(record);
    return updatedRecord;
  });

  const start = useEvent(async (startReason: StartReason) => {
    if (isLoggingOutRef.current || !isTimeTrackingEnabled || isStartingRef.current || isTrackingActiveRef.current) {
      return null;
    }

    isStartingRef.current = true;
    const nowIso = new Date().toISOString();
    const initialRecord = {
      trackingType: TrackingType.STOPWATCH,
      activityType,
      activityId,
      userId,
      mentorId,
      startTime: nowIso,
      startReason,
      endTime: nowIso,
      endReason: EndReason.KEEPALIVE,
      duration: 0,
    };
    try {
      const createdRecord = await createTimeTracking(initialRecord);

      if (isLoggingOutRef.current || !createdRecord?._id) {
        savedTrackingIdRef.current = null;
        return null;
      }

      isTrackingActiveRef.current = true;
      startDisplayInterval();
      startKeepAliveInterval();

      return createdRecord;
    } finally {
      isStartingRef.current = false;
    }
  });

  const restart = useEvent(() => {
    if (!isTimeTrackingEnabled) {
      return null;
    }
    setWatchPaused(false);
    idleTimer.reset();
    return start(StartReason.THERAPIST);
  });

  const stop = useEvent(async () => {
    const endedRecord = await updateRecord(EndReason.THERAPIST);
    isTrackingActiveRef.current = false;
    savedTrackingIdRef.current = null;
    idleTimer.pause();
    setWatchPaused(true);
    stopKeepAliveInterval();
    stopDisplayInterval();
    return endedRecord;
  });

  const close = useEvent(() => {
    updateRecord(EndReason.TASK_CLOSED);
    isTrackingActiveRef.current = false;
    savedTrackingIdRef.current = null;
    stopKeepAliveInterval();
    stopDisplayInterval();
  });

  useEffect(() => {
    return subscribeToTimeTrackerLogout(() => {
      isLoggingOutRef.current = true;
      isTrackingActiveRef.current = false;
      savedTrackingIdRef.current = null;
      stopKeepAliveInterval();
      stopDisplayInterval();
    });
  }, []);

  useEffect(() => {
    start(StartReason.TASK_OPENED);

    return () => {
      close();
    };
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      close();
      window.removeEventListener('pagehide', handlePageHide);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  if (!isTimeTrackingEnabled || hidden) {
    return null;
  }

  return (
    <TimeTrackerContainer>
      <TimeTrackerValue>{formatSeconds(displaySeconds)}</TimeTrackerValue>
      <Tooltip content={watchPaused ? 'Restart watch' : 'Stop watch'} placement="top" hasArrow={false} padding={8}>
        <TimeTrackerActionButton
          onClick={() => {
            if (watchPaused) {
              restart();
            } else {
              stop();
            }
          }}
        >
          {watchPaused ? <MdPlayArrow size={22} /> : <MdPause size={22} />}
        </TimeTrackerActionButton>
      </Tooltip>
    </TimeTrackerContainer>
  );
};

TimeTracker.displayName = 'TimeTracker';

export default TimeTracker;
